import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PasswordField, { pwStrong } from "@/components/PasswordField";
import { useAuth } from "@/context/AuthContext";

function Labeled({ label, children }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
      {children}
    </div>
  );
}

export default function ChangePassword() {
  const { user, setUser } = useAuth();
  const canUsername = user?.role === "admin" || user?.role === "vendor";
  const [uname, setUname] = useState(user?.username || "");
  const [uBusy, setUBusy] = useState(false);
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const saveUsername = async (e) => {
    e.preventDefault();
    setUBusy(true);
    try {
      const { data } = await api.post("/auth/username", { username: uname });
      setUser({ ...user, username: data.username });
      toast.success("Username updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update username");
    } finally { setUBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pwStrong(pw)) return toast.error("Please choose a stronger password (see the requirements).");
    if (pw !== pw2) return toast.error("New passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated");
      setCur(""); setPw(""); setPw2("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update password");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[560px] mx-auto px-6 md:px-12 py-16">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Security</p>
      <h1 className="font-serif text-5xl tracking-tighter">Account &amp; security.</h1>

      {canUsername && (
        <form onSubmit={saveUsername} className="mt-8 border border-foreground p-6 md:p-8 space-y-4">
          <Labeled label="Login username">
            <input value={uname} onChange={(e) => setUname(e.target.value)} placeholder="e.g. rangoli-store" autoCapitalize="none" className="underline-input w-full" />
          </Labeled>
          <p className="font-mono text-[10px] text-muted-foreground">3–30 characters: letters, numbers, dot, dash or underscore. You can sign in with this or your email.</p>
          <button disabled={uBusy || !uname.trim()} className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
            {uBusy ? "Saving…" : "Save username"}
          </button>
        </form>
      )}

      <h2 className="font-serif text-3xl tracking-tighter mt-10">Change password.</h2>
      <form onSubmit={submit} className="mt-4 border border-foreground p-6 md:p-8 space-y-6">
        <Labeled label="Current password">
          <PasswordField value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" placeholder="Leave blank if you signed up with Google" />
        </Labeled>
        <Labeled label="New password">
          <PasswordField value={pw} onChange={(e) => setPw(e.target.value)} strength required />
        </Labeled>
        <Labeled label="Confirm new password">
          <PasswordField value={pw2} onChange={(e) => setPw2(e.target.value)} required />
        </Labeled>
        <button disabled={busy || !pwStrong(pw) || pw !== pw2} className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
